// SubmissionStore.tsx
//@ts-nocheck
import React, { createContext, useContext, useState, ReactNode } from "react";
import { post, get, patch } from "requests";
import { string } from "yup";

type SubmissionStoreState = {
  merchantData: any[];
  dealApplicationId: string;
  userList: any[];
  steps: any[];
  currentJuniorUnderwriter: any;
  currentSeniorUnderwriter: any;
  dealApplicationData: any;
  merchantAndOwners: any;
  setMerchantData: (data: any[]) => void;
  setDealApplicationId: (id: string) => void;
  setUserList: (data: any[]) => void;
  setSteps: (steps: any[]) => void;
  setUnderwriters: (juniorUnderwriter: any, seniorUnderwriter: any) => void;
  brokerParties: any[];
  fetchInitialData: (id: string) => void;
  funderList: any[];
  setFunderList: (funderList: any[]) => void;
  merchantBusinessInfo: any | null;
  updateMerchantBusinessInfo: (data: any) => void;
  saveMerchantData: (data: any) => void;
  storeData: any[];
  setStoreData: (data: any[]) => void;
  brokerName: any;
  brokerId: any;
  funderName: any;
  dealName: any;
  businessName: any;
  stateName: any;
  contractTerms: any;
  setSelectedMerchantId: (id: string | null) => void;
  updateMerchantAndOwners: (data: any) => void;
  affiliatedBusinesses: any[];
  setAffiliatedBusinessesData: (data: any[]) => void;
  dbaInformation: any | null;
  setDbaInformationData: (data: any) => void;
  merchantOwners: any[];
  setMerchantOwnersData: (data: any[]) => void;
  clearAllData: () => void;
  setOwners: (data: any[]) => void;
  reInitializeSubmissionStore: () => void;
  fetchMerchantData: (merchantId: any) => void;
  fetchBrokerData: (appId: any) => void;
  fetchFunderList: (appId: any) => void;
  contractData: any;
  salesRepOptions: any[];
  loadSalesRepsForCurrentBroker: (brokerId: any) => any;
  loadSalesRepsAssignedToCurrentDealApplication: (applicationId: any) => void;
  updateSalesReps: (value: any, dealApplicationId: any) => void;
  salesRepOptions: any[];
  assignedRepIds: any[];
  setBrokerName: (data: any) => void;
  setBrokerId: (data: any) => void;
};

const SubmissionContext = createContext<SubmissionStoreState | undefined>(undefined);

type SubmissionStoreProviderProps = {
  children: ReactNode;
};

type Underwriter = {
  value: number;
  label: string;
};

type Broker = {
  houseParty: boolean;
  phoneNumber: string | null;
  isInhouse: boolean;
  name: string;
  id: number;
  email: string | null;
  isRep: boolean;
};

type Party = {
  name: string;
  isFunder: boolean;
};

type AffiliatedBusiness = {
  id: number;
  name: string;
};

export const SubmissionStoreProvider = ({ children }: SubmissionStoreProviderProps) => {
  const [merchantData, setMerchantData] = useState<any[]>([]);
  const [dealApplicationId, setDealApplicationId] = useState<string>("");
  const [userList, setUserList] = useState<any[]>([]);
  const [steps, setSteps] = useState<any[]>([]);
  const [currentJuniorUnderwriter, setCurrentJuniorUnderwriter] = useState(null);
  const [currentSeniorUnderwriter, setCurrentSeniorUnderwriter] = useState(null);
  const [brokerParties, setBrokerParties] = useState<Broker[]>([]);
  const [funderList, setFunderList] = useState<any[]>([]);
  const [merchantBusinessInfo, setMerchantBusinessInfoState] = useState<any | null>(null);
  const [storeData, setStoreData] = useState<any[]>([]);
  const [brokerName, setBrokerName] = useState<string | null>(null);
  const [brokerId, setBrokerId] = useState<any | null>(null);

  const [funderName, setFunderName] = useState<string | null>(null);
  const [dealName, setDealName] = useState<string | null>(null);
  const [businessName, setBusinessName] = useState<string | null>(null);
  const [stateName, setStateName] = useState<string | null>(null);
  const [selectedMerchantId, setSelectedMerchantId] = useState<string | null>(null);
  const [merchantAndOwners, setMerchantAndOwners] = useState<any | null>(null);
  const [affiliatedBusinesses, setAffiliatedBusinesses] = useState<any[]>([]);
  const [dbaInformation, setDbaInformation] = useState<any | null>(null);
  const [merchantOwners, setMerchantOwnersData] = useState<any[]>([]);
  const [owners, setOwners] = useState<any[]>([]);
  const [dealApplicationData, setDealApplicationData] = useState<any[]>(null);
  const [contractData, setContractData] = useState<any>(null);
  const [contractLoading, setContractLoading] = useState(true);
  const [contractTerms, setContractTerms] = useState<any[]>(null);
  const [submissionLoader, setSubmissionLoader] = useState<boolean>(false);
  const [salesRepOptions, setSalesRepOptions] = useState<any[]>([]);
  const [assignedRepIds, setAssignedRepIds] = useState<any[]>([]);

  const reInitializeSubmissionStore = () => {
    setMerchantData([]);
    setDealApplicationId("");
    setUserList([]);
    setSteps([]);
    setCurrentJuniorUnderwriter(null);
    setCurrentSeniorUnderwriter(null);
    setBrokerParties([]);
    setFunderList([]);
    setMerchantBusinessInfoState(null);
    setStoreData([]);
    setBrokerName(null);
    setBrokerId(null);
    setFunderName(null);
    setDealName(null);
    setBusinessName(null);
    setStateName(null);
    setSelectedMerchantId(null);
    setMerchantAndOwners(null);
    setAffiliatedBusinesses([]);
    setDbaInformation(null);
    setMerchantOwnersData([]);
    setOwners([]);
    setDealApplicationData(null);
    setContractTerms(null);
    setAssignedRepIds([]);
    setSalesRepOptions([]);
  };

  const setUnderwriters = (
    juniorUnderwriter: Underwriter | null,
    seniorUnderwriter: Underwriter | null
  ) => {
    setCurrentJuniorUnderwriter(juniorUnderwriter);
    setCurrentSeniorUnderwriter(seniorUnderwriter);
  };

  const saveMerchantData = async (updatedInfo: any) => {
    try {
      console.log("Saving merchant data:", updatedInfo);
      if (!updatedInfo || !updatedInfo.merchantId) {
        throw new Error("Invalid merchant data: Missing merchantId");
      }

      if (!updatedInfo.tenantId) {
        const tenantId = localStorage.getItem("tenantId");
        updatedInfo.tenantId = Number(tenantId);
      }

      const response = await post("merchant/updateOrCreateMerchantAndUsers", updatedInfo);

      console.log("API response:", response);
      setMerchantBusinessInfoState(updatedInfo);
      return response;
    } catch (error) {
      console.error("Failed to save merchant data:", error);
      throw error;
    }
  };

  const loadSalesRepsForCurrentBroker = async (brokerId: any) => {
    if (!brokerId) {
      return;
    }
    const response = await get(`getPartyUsers/${brokerId}`);
    const salesRepList = response?.users?.map((user) => {
      return { label: user.name, value: user.id };
    });
    setSalesRepOptions(salesRepList || []);
  };

  const updateSalesReps = (value: any, dealApplicationId: any) => {
    const selected = value || [];

    const newSelectedIds = selected.map((user: any) => Number(user.value));
    console.log("newSelectedIds", newSelectedIds);

    setAssignedRepIds(newSelectedIds);

    patch(`dealApplications/${dealApplicationId}`, {
      sales_reps_jb: selected.map((user: any) => ({
        id: user.value,
        name: user.label,
      })),
    }).then(() => {
      loadSalesRepsAssignedToCurrentDealApplication(dealApplicationId);
    });
  };

  const loadSalesRepsAssignedToCurrentDealApplication = async (applicationId: any) => {
    const assignedReps = await get(`dealApplications/${applicationId}/salesReps`);
    const assignedRepIds = assignedReps?.data?.map((u) => Number(u.id));
    setAssignedRepIds(assignedRepIds || []);
  };

  const fetchInitialData = async (id?: string) => {
    if (!id) {
      console.log("No ID provided to fetch initial data.");
      return;
    }

    setSubmissionLoader(true);

    try {
      const stepsPromise = post("/steps/getStepsByDealApplicationId", { id });
      const brokerPromise = get(`getBroker/${id}`);
      const contractPromise = get(`getContractTerms/${id}`);
      const dealPromise = get(`getDealApplication/${id}`);
      loadSalesRepsAssignedToCurrentDealApplication(id);

      get("fetchAllUsers").then(setUserList).catch(console.error);
      get("fetchAllParties")
        .then((data) => {
          const filtered = Array.isArray(data)
            ? data.filter((party: Party) => party.isFunder)
            : Object.values(data).filter((party: Party) => party.isFunder);
          setFunderList(filtered);
        })
        .catch(console.error);
      get(`fetchAllMerchantsAndOwners/${localStorage.getItem("tenantId")}`)
        .then(setMerchantData)
        .catch(console.error);
      get(`fetchBrokerParties/${localStorage.getItem("tenantId")}`)
        .then((data) => setBrokerParties(data.brokers || []))
        .catch(console.error);

      const [stepsData, brokerData, contractData, dealData] = await Promise.all([
        stepsPromise,
        brokerPromise,
        contractPromise,
        dealPromise,
      ]);
      setSteps(stepsData.result);
      setContractData(contractData);

      if (dealData) {
        setDealApplicationData(dealData);
        setDealName(dealData.representativeName);
        const merchantId = dealData?.partyId;
        if (merchantId) {
          await fetchMerchantData(merchantId);
        } else {
          setMerchantBusinessInfoState(null);
          setMerchantAndOwners([]);
          setDbaInformationData([]);
          setAffiliatedBusinessesData([]);
        }
      }

      if (brokerData) {
        setBrokerName(brokerData.brokerName);
        setBrokerId(brokerData.brokerId);
        if (brokerData.brokerId) loadSalesRepsForCurrentBroker(brokerData.brokerId);
      }

      if (contractData?.contractTerms?.funder) {
        setFunderName(contractData?.contractTerms?.funder?.name);
      }
    } catch (error) {
      console.error("Error fetching core data:", error);
    } finally {
      setSubmissionLoader(false);
    }
  };

  const fetchBrokerData = async (id?: string) => {
    const brokerData = await get(`getBroker/${id}`);
    get(`fetchBrokerParties/${localStorage.getItem("tenantId")}`)
      .then((data) => setBrokerParties(data.brokers || []))
      .catch(console.error);
    if (brokerData) {
      setBrokerName(brokerData.brokerName);
      setBrokerId(brokerData.brokerId);
      if (brokerData.brokerId) loadSalesRepsForCurrentBroker(brokerData.brokerId);
    }
  };

  const fetchFunderList = async (id?: string): Promise<Party[] | undefined> => {
    try {
      const data = await get("fetchAllParties");
      const filtered = Array.isArray(data)
        ? data.filter((party: Party) => party.isFunder)
        : Object.values(data).filter((party: Party) => party.isFunder);
      setFunderList(filtered);
      return filtered;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const fetchMerchantData = async (merchantId: string, reloadAll: boolean = true) => {
    try {
      console.log("=== FETCH MERCHANT DATA DEBUG ===");
      console.log("Merchant ID:", merchantId);
      console.log("Reload All:", reloadAll);

      const response = await get(`fetchMerchantAndOwners/${merchantId}`);
      console.log("fetchMerchantAndOwners response:", response);

      if (!response) {
        console.error("API response is null or undefined:", response);
        return;
      }

      const {
        merchantId: id,
        merchantName,
        physicalAddress,
        federalTaxId,
        dba,
        users,
        affiliatedBusinesses,
      } = response;

      const country = physicalAddress?.countryCode || "";
      console.log("Extracted country from response:", country);
      console.log("Physical address:", physicalAddress);

      const state = physicalAddress?.state;
      console.log("Updating merchant business info with:", { merchantId: id, merchantName, state, federalTaxId, country });
      updateMerchantBusinessInfo({ merchantId: id, merchantName, state, federalTaxId, users, country });
      setMerchantAndOwners(response);
      setDbaInformationData(dba || []);

      if (!response || !response.users || response.users.length === 0) {
        console.error("No users found in the response:", response);
        setAffiliatedBusinessesData([]);
        return;
      }

      if (!reloadAll) return;
      const userIds = Array.from(
        new Set(response?.users.map((user: { userId: number }) => user.userId))
      );

      if (userIds.length === 0) {
        setAffiliatedBusinessesData([]);
        return;
      }
      try {
        const affiliatedBusinessResponse = await post("fetchAffiliatedBusinessByOwnerIds", {
          userIds,
        });

        if (affiliatedBusinessResponse && Array.isArray(affiliatedBusinessResponse)) {
          setAffiliatedBusinessesData(affiliatedBusinessResponse);
        } else {
          setAffiliatedBusinessesData([]);
        }
      } catch (error) {
        console.error("Error fetching affiliated businesses:", error);
      }
    } catch (err) {
      console.error("Error in fetchMerchantAndOwners API call:", err);
    }
  };

  const updateMerchantBusinessInfo = (data: {
    merchantId: string;
    merchantName: string;
    state: string;
    federalTaxId: string;
    country?: string;
  }) => {
    setMerchantBusinessInfoState(data);
  };

  const updateMerchantAndOwners = (data: any) => {
    setMerchantAndOwners(data);
  };

  const setAffiliatedBusinessesData = (data: AffiliatedBusiness[]) => {
    setAffiliatedBusinesses(data);
  };

  const setDbaInformationData = (data: any) => {
    setDbaInformation(data);
  };

  const clearAllData = () => {
    setStoreData(null);
    setMerchantBusinessInfoState(null);
    setMerchantOwnersData([]);
    setMerchantAndOwners([]);
    setDbaInformation(null);
    setOwners([]);
  };

  return (
    <SubmissionContext.Provider
      value={{
        merchantData,
        setMerchantData,
        dealApplicationId,
        setDealApplicationId,
        userList,
        setUserList,
        steps,
        setSteps,
        currentJuniorUnderwriter,
        currentSeniorUnderwriter,
        setUnderwriters,
        brokerParties,
        fetchInitialData,
        funderList,
        setFunderList,
        merchantAndOwners,
        merchantBusinessInfo,
        updateMerchantBusinessInfo,
        saveMerchantData,
        storeData,
        setStoreData,
        brokerName,
        brokerId,
        funderName,
        contractTerms,
        fetchMerchantData,
        fetchBrokerData,
        fetchFunderList,
        dealName,
        dealApplicationData,
        businessName,
        stateName,
        selectedMerchantId,
        setSelectedMerchantId,
        updateMerchantAndOwners,
        affiliatedBusinesses,
        setAffiliatedBusinessesData,
        dbaInformation,
        setDbaInformationData,
        merchantOwners,
        setMerchantOwnersData,
        clearAllData,
        setOwners,
        reInitializeSubmissionStore,
        contractData,
        submissionLoader,
        loadSalesRepsForCurrentBroker,
        loadSalesRepsAssignedToCurrentDealApplication,
        updateSalesReps,
        salesRepOptions,
        assignedRepIds,
        setBrokerId,
        setBrokerName,
      }}
    >
      {children}
    </SubmissionContext.Provider>
  );
};

export const useSubmissionStore = (): SubmissionStoreState => {
  const context = useContext(SubmissionContext);
  if (!context) {
    throw new Error("useSubmissionStore must be used within a SubmissionStoreProvider");
  }
  return context;
};
